import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    Alert,
    CircularProgress,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { useSendPushToUser, useSendPushToGroup, usePushTargetPreview } from '../../hooks/usePush';
import { PushTargetType, PushTargetTypeLabels } from '../../types/push';
import { ImageUpload } from '../common/ImageUpload';

interface SendPushDialogProps {
    open: boolean;
    onClose: () => void;
    mode: 'individual' | 'group';
    userId?: number;
    userEmail?: string;
}

export default function SendPushDialog({
    open,
    onClose,
    mode,
    userId,
    userEmail,
}: SendPushDialogProps) {
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [imageUrl, setImageUrl] = useState<string | undefined>(undefined);
    const [targetType, setTargetType] = useState<PushTargetType>(PushTargetType.ALL);
    const [resultMessage, setResultMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    const sendToUser = useSendPushToUser();
    const sendToGroup = useSendPushToGroup();

    const { data: preview, isLoading: previewLoading } = usePushTargetPreview(
        targetType,
        mode === 'group' && open
    );

    useEffect(() => {
        if (open) {
            setTitle('');
            setBody('');
            setImageUrl(undefined);
            setTargetType(PushTargetType.ALL);
            setResultMessage(null);
        }
    }, [open]);

    const handleSend = async () => {
        if (!title.trim() || !body.trim()) {
            setResultMessage({ type: 'error', text: '제목과 내용을 입력해주세요.' });
            return;
        }

        try {
            if (mode === 'individual' && userId) {
                const result = await sendToUser.mutateAsync({
                    userId,
                    data: { title, body, imageUrl },
                });
                if (result.successCount > 0) {
                    setResultMessage({
                        type: 'success',
                        text: `푸시 발송 성공!`,
                    });
                } else {
                    setResultMessage({
                        type: 'error',
                        text: '푸시 발송에 실패했습니다. (FCM 토큰 없거나 비활성화)',
                    });
                }
            } else {
                const result = await sendToGroup.mutateAsync({
                    targetType,
                    title,
                    body,
                    imageUrl,
                });
                setResultMessage({
                    type: 'success',
                    text: `발송 완료! 성공: ${result.successCount}명, 실패: ${result.failureCount}명`,
                });
            }
        } catch {
            setResultMessage({
                type: 'error',
                text: '발송 중 오류가 발생했습니다.',
            });
        }
    };

    const isLoading = sendToUser.isPending || sendToGroup.isPending;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {mode === 'individual' ? '개별 푸시 발송' : '그룹 푸시 발송'}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {mode === 'individual' && userEmail && (
                        <Alert severity="info">
                            <strong>{userEmail}</strong>에게 푸시를 발송합니다.
                        </Alert>
                    )}

                    {mode === 'group' && (
                        <FormControl fullWidth>
                            <InputLabel>발송 대상</InputLabel>
                            <Select
                                value={targetType}
                                label="발송 대상"
                                onChange={(e) => setTargetType(e.target.value as PushTargetType)}
                            >
                                {Object.entries(PushTargetTypeLabels)
                                    .filter(([key]) => key !== PushTargetType.INDIVIDUAL)
                                    .map(([value, label]) => (
                                        <MenuItem key={value} value={value}>
                                            {label}
                                        </MenuItem>
                                    ))}
                            </Select>
                            {previewLoading ? (
                                <Typography variant="caption" sx={{ mt: 1 }}>
                                    <CircularProgress size={12} sx={{ mr: 1 }} />
                                    대상 수 조회 중...
                                </Typography>
                            ) : preview && (
                                <Typography variant="caption" sx={{ mt: 1 }}>
                                    발송 대상: <strong>{preview.count}명</strong>
                                </Typography>
                            )}
                        </FormControl>
                    )}

                    <TextField
                        label="제목"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        fullWidth
                        inputProps={{ maxLength: 100 }}
                        helperText={`${title.length}/100`}
                    />

                    <TextField
                        label="내용"
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        fullWidth
                        multiline
                        rows={4}
                        inputProps={{ maxLength: 500 }}
                        helperText={`${body.length}/500`}
                    />

                    <ImageUpload
                        value={imageUrl}
                        onChange={setImageUrl}
                        folder="push"
                        label="이미지 (선택)"
                        helperText="푸시 알림에 표시될 이미지입니다. 권장 크기: 1024x512px"
                    />

                    {resultMessage && (
                        <Alert severity={resultMessage.type}>
                            {resultMessage.text}
                        </Alert>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>취소</Button>
                <Button
                    onClick={handleSend}
                    variant="contained"
                    startIcon={isLoading ? <CircularProgress size={16} /> : <SendIcon />}
                    disabled={isLoading || !title.trim() || !body.trim()}
                >
                    {isLoading ? '발송 중...' : '발송'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
